/**
 * Block 12 PR-Hotfix-2B Phase B4 Step 8 — VitrineCompound primitive (Bug 12).
 *
 * Compound row для row.id === 'aluminum-glass-vitrine' (Витрина в алюм.
 * профиле). Заменяет generic ToggleableRow для Витрина, потому что Витрина
 * имеет 3 размерных параметра вместо одного `qty`:
 *   - `length` (мм) — длина витрины
 *   - `height` (мм) — высота витрины
 *   - `pricePerM2` (₸/м²) — цена за м² (вместо `pricePerUnit`)
 *
 * Площадь и стоимость считаются автоматически (read-only):
 *   - `areaM2 = (length × height) / 1_000_000`
 *   - `cost = areaM2 × pricePerM2`
 *
 * Когда `enabled === true`, под label рендерится жёлтая кнопка «Найти и узнать
 * цену» → /partners (PR-calc-buttons-unify, ADR-0099 — Container делает
 * forcePersist + navigate) — Витрина это специзделие, не готовый компонент.
 *
 * Опциональный `hint` prop — для TV-зона conditional warning когда vitrineOn
 * + hasShelf=true (фасад шкафа учитывается только по двум глубинам).
 *
 * Прототип ref:
 *   - Kitchen `screen-2.12.2:459-486`
 *   - BathTabletop `screen-2.12.4:446-486` (если присутствует)
 *   - TV-зона `screen-2.12.7:486-518` (если присутствует)
 *   - Cabinet `screen-2.12.3:462-486` («Особые конструкции»)
 *
 * Контракт: при `enabled === false` рендерится только toggle button (как
 * ToggleableRow off-state). При `enabled === true` рендерится full UI:
 * deeplink + 3 inputs + readonly area + readonly cost.
 *
 * Reference: Hotfix-2B TZ §5 Step 8, Audit Round 2 Bug 12, ADR-0020 §Q1.
 */

import { NumberInput } from '@/components/forms/NumberInput';
import { ADDITIONAL_META } from '../additionalMeta';
import { computeVitrineAreaM2, computeVitrineCost } from '../helpers/vitrineFormulas';

/**
 * Label берётся из `ADDITIONAL_META['aluminum-glass-vitrine']` — single source
 * of truth для UI rendering + persistence (useAnketaPersist encode). Раньше
 * был хардкод 'Витрина в алюм. профиле' (per Kitchen prototype 2.12.2:462), но
 * это вело к drift между display и encoded freeItem.name ('Витрина в
 * алюминиевом профиле' per PRD §10.5 + 2.12.4/2.12.7 prototypes). Phase B4
 * Step 10 reviewer caught это; align на ADDITIONAL_META.label (полная форма).
 */
// Non-null assertion safe per additionalMeta.test.ts invariant
// (ADDITIONAL_META keys === CalculatorAdditionalSchema.options, two-way drift check).
const VITRINE_LABEL = ADDITIONAL_META['aluminum-glass-vitrine']!.label;

export interface VitrineCompoundProps {
  enabled: boolean;
  /** Длина (мм). */
  length: number;
  /** Высота (мм). */
  height: number;
  /** Цена за м² (₸). */
  pricePerM2: number;
  /**
   * Patch handler. Caller dispatch'ит actions (Phase B4 Step 9 wire) либо
   * передаёт partial setter (Step 8 standalone primitive).
   */
  onChange: (patch: {
    enabled?: boolean;
    length?: number;
    height?: number;
    pricePerM2?: number;
  }) => void;
  /**
   * Optional warning hint — рендерится под cost когда `enabled === true`.
   * Текущий use-case: TV-зона conditional warning когда `hasShelf=true`.
   */
  hint?: string;
  /** PR-calc-buttons-unify (ADR-0099) — жёлтая кнопка «Найти и узнать цену» → /partners. */
  onDeeplink?: () => void | Promise<void>;
  /**
   * PR-H1 Round 3 — brand input для Витрины. Render gated на `onBrandChange`
   * (caller decides). Match design pattern из ResultRow (P-044/P-045/P-039).
   */
  brand?: string;
  onBrandChange?: (brand: string) => void;
}

export function VitrineCompound({
  enabled,
  length,
  height,
  pricePerM2,
  onChange,
  hint,
  onDeeplink,
  brand,
  onBrandChange,
}: VitrineCompoundProps) {
  if (!enabled) {
    return (
      <button
        type="button"
        onClick={() => onChange({ enabled: true })}
        data-testid="toggle-add-aluminum-glass-vitrine"
        className="min-h-tap rounded-xl border-[1.5px] border-dashed border-accent bg-card text-ink font-semibold text-base px-4 text-left flex items-center justify-between focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
      >
        <span>+ Добавить «{VITRINE_LABEL}»</span>
      </button>
    );
  }

  const areaM2 = computeVitrineAreaM2(length, height);
  const cost = computeVitrineCost(length, height, pricePerM2);

  return (
    <div
      className="rounded-2xl bg-card border border-accent p-4 flex flex-col gap-3"
      data-testid="vitrine-compound"
    >
      <div className="flex justify-between items-start gap-3">
        <h4 className="text-ink font-semibold text-base">{VITRINE_LABEL}</h4>
        <div
          className="text-ink font-bold text-base whitespace-nowrap"
          aria-live="polite"
          data-testid="vitrine-compound-cost"
        >
          {Math.round(cost).toLocaleString('ru-RU')} ₸
        </div>
      </div>

      {onDeeplink ? (
        <button
          type="button"
          onClick={() => {
            void onDeeplink();
          }}
          className="min-h-tap rounded-xl bg-cta text-on-cta font-semibold text-base px-4 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
          data-testid="vitrine-find-price"
        >
          Найти и узнать цену
        </button>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <NumberInput
          name="vitrine-length"
          label="Длина (мм)"
          value={length}
          onChange={(v) => onChange({ length: v ?? 0 })}
          min={0}
          step={50}
        />
        <NumberInput
          name="vitrine-height"
          label="Высота (мм)"
          value={height}
          onChange={(v) => onChange({ height: v ?? 0 })}
          min={0}
          step={50}
        />
      </div>

      <div className="grid grid-cols-2 gap-3 items-end">
        <div data-testid="vitrine-compound-area">
          <label className="block text-xs text-muted mb-1">Площадь</label>
          <div
            className="min-h-tap rounded-xl border border-accent bg-icon-bg px-3 flex items-center text-ink font-semibold text-base"
            aria-label={`Площадь витрины: ${areaM2.toFixed(2)} м²`}
            aria-live="polite"
          >
            {areaM2.toFixed(2)} м²
          </div>
        </div>
        <NumberInput
          name="vitrine-price-per-m2"
          label="Цена за м²"
          value={pricePerM2}
          onChange={(v) => onChange({ pricePerM2: v ?? 0 })}
          unit="₸"
          min={0}
        />
      </div>

      {typeof onBrandChange === 'function' ? (
        <div>
          <label
            htmlFor="vitrine-compound-brand-input"
            className="block text-sm font-medium text-ink mb-1"
          >
            Бренд
          </label>
          <input
            id="vitrine-compound-brand-input"
            type="text"
            aria-label={`Бренд для ${VITRINE_LABEL}`}
            placeholder="Бренд производителя"
            value={brand ?? ''}
            onChange={(e) => onBrandChange?.(e.target.value)}
            maxLength={100}
            className="w-full min-h-tap rounded-xl border-[1.5px] border-accent bg-card px-3 text-ink text-base placeholder:text-muted/60 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
            data-testid="vitrine-compound-brand"
          />
        </div>
      ) : null}

      {hint ? (
        <p className="text-xs text-muted italic" role="note" data-testid="vitrine-compound-hint">
          {hint}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => onChange({ enabled: false })}
        data-testid="toggle-remove-aluminum-glass-vitrine"
        className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-muted font-semibold text-sm px-4 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none self-end"
      >
        Убрать
      </button>
    </div>
  );
}
